/**
 * Cursor 风格代码块 + 复制（阻止冒泡，避免与消息折叠点击冲突）
 * enlighter-origin：子比等主题会对 pre code 跑 EnlighterJS，加此类可排除，避免双工具栏/双复制
 */

import React, { useCallback, useState } from 'react';

function sanitizeLang(lang: string): string {
  const s = lang.trim().replace(/[^a-zA-Z0-9+#.-]/g, '');
  return s.toLowerCase() || 'text';
}

export interface CodeBlockProps {
  language: string;
  code: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ language, code }) => {
  const [copied, setCopied] = useState(false);
  const displayLang = sanitizeLang(language);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        try {
          const ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        } catch {
          /* ignore */
        }
      }
    },
    [code]
  );

  return (
    <div
      className="sse-cursor-code-block"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="sse-cursor-code-toolbar">
        <span className="sse-cursor-code-lang">{displayLang}</span>
        <button
          type="button"
          className="sse-cursor-code-copy"
          onClick={handleCopy}
          aria-label={copied ? '已复制' : '复制代码'}
        >
          {copied ? (
            <>
              <svg
                className="sse-cursor-code-copy-svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>已复制</span>
            </>
          ) : (
            <>
              <svg
                className="sse-cursor-code-copy-svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              <span>复制</span>
            </>
          )}
        </button>
      </div>
      <pre className="sse-cursor-code-pre">
        <code
          className={`sse-cursor-code-inner enlighter-origin language-${displayLang.replace(/[^a-z0-9_-]/g, '') || 'text'}`}
        >
          {code}
        </code>
      </pre>
    </div>
  );
};
